import { z } from 'zod';

import { DISTRIBUTION_TERRITORIES } from '~settings/api/lib/types';
import { DEAL_REGION_SCOPE } from '~settings/constants';
import { sortByPriority } from '~settings/utils/roles';

export const BaseEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type BaseEntity = z.infer<typeof BaseEntitySchema>;

export const ResourceTypeSchema = z.enum(['LABEL', 'RESOURCE_GROUP']);
export type ResourceType = z.infer<typeof ResourceTypeSchema>;

export const ResourceSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  type: ResourceTypeSchema,
});
export type Resource = z.infer<typeof ResourceSchema>;

export const RoleSchema = z.enum([
  'RELEASE_APPROVER',
  'VIDEO_APPROVER',
  'REPORTER',
  'INVENTORY_MANAGER',
  'LABEL_ADMIN',
  'RELEASE_COORDINATOR',
  'VIDEO_COORDINATOR',
  // roles below are deprecated
  'OPS_ADMIN',
  'TECH_ADMIN',
  'SUPPORT_ADMIN',
  'RELEASE_MANAGER',
  'VIDEO_MANAGER',
]);

export type Role = z.infer<typeof RoleSchema>;

export const UserTypeSchema = z.enum([
  'SUPERADMIN',
  'ADMIN',
  'MANAGER',
  'VIEWER',
  'EXTERNAL',
  // deprecated user types below
  'INTERNAL',
]);
export type UserType = z.infer<typeof UserTypeSchema>;

export const PaginatedResponseSchema = <TData>(data: z.ZodType<TData>) =>
  z.object({
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
    data: z.array(data),
  });
export type PaginatedResponse<TData> = z.infer<
  ReturnType<typeof PaginatedResponseSchema<TData>>
>;

export const UserTypeListSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type UserTypeList = z.infer<typeof UserTypeListSchema>;
export const UserTypeListResponseSchema =
  PaginatedResponseSchema(UserTypeListSchema);
export type UserTypeListResponse = z.infer<typeof UserTypeListResponseSchema>;

export const UserRoleSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type UserRole = z.infer<typeof UserRoleSchema>;
export const UserRoleListResponseSchema =
  PaginatedResponseSchema(UserRoleSchema);
export type UserRoleListResponse = z.infer<typeof UserRoleListResponseSchema>;

export const LabelSchema = z.object({
  id: z.string(),
  name: z.string(),
  isParentLabel: z.boolean(),
  parent: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  roles: z.array(RoleSchema).transform(sortByPriority),
});
export type Label = z.infer<typeof LabelSchema>;

const LabelGroupSchema = BaseEntitySchema.extend({
  authApiResourceGroupId: z.string(),
});

export type LabelGroup = z.infer<typeof LabelGroupSchema>;

export const BaseUserSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  isActive: z.boolean(),
  isInternal: z.boolean().prefault(false).optional(),
  email: z.email(),
  lastLogin: z.string().nullable(),
  userType: UserTypeSchema.optional(),
  roleResources: z.partialRecord(RoleSchema, z.array(ResourceSchema)),
});

export const UserListItemSchema = BaseUserSchema.extend({
  labels: z.array(LabelSchema),
  labelGroups: z.array(LabelGroupSchema).nullable(),
  roles: z.array(RoleSchema),
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

export const UserListResponseSchema =
  PaginatedResponseSchema(UserListItemSchema);
export type UserListResponse = z.infer<typeof UserListResponseSchema>;

export const RolesMapSchema = z.partialRecord(
  RoleSchema,
  z.object({
    labels: z.array(BaseEntitySchema),
    labelGroups: z.array(LabelGroupSchema),
  }),
);

export const UserDetailSchema = BaseUserSchema.extend({
  phone: z.string().nullable(),
  labelGroups: z.array(LabelGroupSchema).optional().nullable(),
  jobTitle: z.string(),
  organization: z.string().nullable(),
  rolesMap: RolesMapSchema.nullable().optional(),
});

export type UserDetail = z.infer<typeof UserDetailSchema>;

export const OktaUserSchema = z.looseObject({
  profile: z.looseObject({
    firstName: z.string(),
    lastName: z.string(),
    mobilePhone: z.string().optional().nullable(),
  }),
});

export type OktaUser = z.infer<typeof OktaUserSchema>;

export const DealRegionScopeSchema = z.enum(DEAL_REGION_SCOPE);
export const DistributorListItemSchema = z.object({
  id: z.string(),
  us: z.string(),
  exUs: z.string(),
});
export type DistributorListItem = z.infer<typeof DistributorListItemSchema>;

export const DistributorListResponseSchema = PaginatedResponseSchema(
  DistributorListItemSchema,
);
export type DistributorListResponse = z.infer<
  typeof DistributorListResponseSchema
>;

export const PresentationLabelItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type PresentationLabelItem = z.infer<typeof PresentationLabelItemSchema>;
export const PresentationLabelListResponseSchema = PaginatedResponseSchema(
  PresentationLabelItemSchema,
);
export type PresentationLabelListResponse = z.infer<
  typeof PresentationLabelListResponseSchema
>;

export const LabelListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  isParentLabel: z.boolean(),
  usersCount: z.number(),
  sublabelsCount: z.number(),
  labelGroups: z.array(LabelGroupSchema).nullable(),
  parent: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  roles: z.array(RoleSchema),
});
export type LabelListItem = z.infer<typeof LabelListItemSchema>;

export const LabelListResponseSchema =
  PaginatedResponseSchema(LabelListItemSchema);
export type LabelListResponse = z.infer<typeof LabelListResponseSchema>;

export const LabelDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  parentId: z.string().nullable(),
  parentName: z.string().nullable(),
  weaLabelCode: z.string(),
  dealOwner: z.string(),
  pmo: z.string().nullable(),
  profitCenter: z.string().nullable(),
  marketingCompany: z.string().nullable(),
  usLabelCode: z.string().nullable(),
  dealScope: z.string().nullable(),
  gpiNumber: z.string().nullable(),
  dealId: z.string().nullable(),
  scopeId: z.string().nullable(),
  dealRegionScope: DealRegionScopeSchema,
  facebookReference: z.string().nullable(),
  twitterReference: z.string().nullable(),
  youtubeReference: z.string().nullable(),
  instagramReference: z.string().nullable(),
  website: z.string().nullable(),
  distributionGroups: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        distributionGroupCountries: z.array(
          z.object({
            country: z
              .object({
                id: z.string(),
                name: z.string(),
              })
              .nullable(),
          }),
        ),
        type: z.enum(DISTRIBUTION_TERRITORIES),
        notes: z.string().nullable(),
      }),
    )
    .nullable(),
  labelGroups: z.array(LabelGroupSchema).nullable(),
  numberOfLabelGroups: z.number().nullable(),
  sublabelsCount: z.number(),
  usersCount: z.number(),

  wmiLabelCode: z.string(),
  repertoireOwner: z.string(),
  wbsElementNumber: z.string(),
  gvlCode: z.string(),
  labelPresentation: z.string(),
  distributorId: z.string(),
  allowTimedRelease: z.boolean(),
  asylum: z.boolean(),
  directPassQc: z.boolean(),
  labelIsrcPrefix: z.string().nullable(),
  digitalProduct: z.boolean().nullable(),
});
export type LabelDetail = z.infer<typeof LabelDetailSchema>;

export const LabelGroupListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdByName: z.string(),
  notes: z.string().nullable(),
  numberOfLabels: z.number(),
  numberOfUsers: z.number(),
});
export type LabelGroupListItem = z.infer<typeof LabelGroupListItemSchema>;

export const LabelGroupListResponseSchema = PaginatedResponseSchema(
  LabelGroupListItemSchema,
);
export type LabelGroupListResponse = z.infer<
  typeof LabelGroupListResponseSchema
>;

export const OrganizationListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  shortTitle: z.string().optional(),
  color: z.string().optional(),
});

export type OrganizationListItem = z.infer<typeof OrganizationListItemSchema>;

export const OrganizationListResponseSchema = z.array(
  OrganizationListItemSchema,
);

export type OrganizationListResponse = z.infer<
  typeof OrganizationListResponseSchema
>;

export type DealOwnerListItem = z.infer<typeof BaseEntitySchema>;

export const DealOwnerListResponseSchema = z.array(BaseEntitySchema);

export type DealOwnerListResponse = z.infer<typeof DealOwnerListResponseSchema>;

export const CountryGroupSchema = z.object({
  id: z.string(), // continent ID
  name: z.string(),
  countries: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
    }),
  ),
});

export const CountryGroupListSchema = z.object({
  countryGroups: z.array(CountryGroupSchema),
});

export type CountryGroupListResponse = z.infer<typeof CountryGroupListSchema>;
export type CountryGroup = z.infer<typeof CountryGroupSchema>;

export const LabelGroupUserSchema = z.object({
  id: z.string(),
  fullName: z.string(),
});

export const LabelGroupDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  notes: z.string().nullable(),
  users: z.array(LabelGroupUserSchema),
  labels: z.array(BaseEntitySchema),
});

export type LabelGroupDetail = z.infer<typeof LabelGroupDetailSchema>;
